// apps/web/src/api/lead-sources.ts — typed wrappers around the admin
//   `/api/lead-sources/*` surface (#1078/#1080). Every entry routes
//   through `request<T>` so the shared 401-retry / ApiClientError
//   surface stays uniform with the rest of the admin API clients.
//
// @aiSecurity the plaintext HMAC secret is ONLY returned by `create`
//   and `rotateSecret`. Callers MUST surface it to the user inside a
//   reveal modal and never persist it in client state beyond the
//   visible flow.
import { z } from 'zod';
import {
  LeadSourceSchema,
  LeadSourceSecretRevealSchema,
  LeadSourceDeliveryLogSchema,
  type CreateLeadSourceInput,
  type LeadSource,
  type LeadSourceDeliveryLog,
  type LeadSourceDeliveryLogQuery,
  type LeadSourceDeliveryOutcome,
  type LeadSourceListQuery,
  type LeadSourceSecretReveal,
  type UpdateLeadSourceInput,
} from '@magimp/shared';
import { request } from './client.js';

const LeadSourceListResponseSchema = z.array(LeadSourceSchema);
const LeadSourceDeliveryLogListResponseSchema = z.array(LeadSourceDeliveryLogSchema);

function withQuery(path: string, qs: string): string {
  return qs.length > 0 ? `${path}?${qs}` : path;
}

export function listLeadSources(query: Partial<LeadSourceListQuery> = {}): Promise<LeadSource[]> {
  const params = new URLSearchParams();
  if (query.includeArchived !== undefined) {
    params.set('includeArchived', String(query.includeArchived));
  }
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  return request<LeadSource[]>(
    withQuery('/lead-sources', params.toString()),
    { method: 'GET' },
    {},
    LeadSourceListResponseSchema,
  );
}

export function fetchLeadSource(code: string): Promise<LeadSource> {
  return request<LeadSource>(
    `/lead-sources/${encodeURIComponent(code)}`,
    { method: 'GET' },
    {},
    LeadSourceSchema,
  );
}

export function createLeadSource(body: CreateLeadSourceInput): Promise<LeadSourceSecretReveal> {
  return request<LeadSourceSecretReveal>(
    '/lead-sources',
    {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    },
    {},
    LeadSourceSecretRevealSchema,
  );
}

export function patchLeadSource(code: string, body: UpdateLeadSourceInput): Promise<LeadSource> {
  return request<LeadSource>(
    `/lead-sources/${encodeURIComponent(code)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    },
    {},
    LeadSourceSchema,
  );
}

export function rotateLeadSourceSecret(code: string): Promise<LeadSourceSecretReveal> {
  return request<LeadSourceSecretReveal>(
    `/lead-sources/${encodeURIComponent(code)}/rotate-secret`,
    { method: 'POST' },
    {},
    LeadSourceSecretRevealSchema,
  );
}

export function archiveLeadSource(code: string): Promise<LeadSource> {
  return request<LeadSource>(
    `/lead-sources/${encodeURIComponent(code)}`,
    { method: 'DELETE' },
    {},
    LeadSourceSchema,
  );
}

export function listLeadSourceDeliveryLogs(
  code: string,
  query: Partial<LeadSourceDeliveryLogQuery> = {},
): Promise<LeadSourceDeliveryLog[]> {
  const params = new URLSearchParams();
  if (query.outcome) params.set('outcome', query.outcome as LeadSourceDeliveryOutcome);
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  return request<LeadSourceDeliveryLog[]>(
    withQuery(`/lead-sources/${encodeURIComponent(code)}/delivery-logs`, params.toString()),
    { method: 'GET' },
    {},
    LeadSourceDeliveryLogListResponseSchema,
  );
}
